import { useState, useEffect, useRef, useCallback } from 'react';
import { Camera, FileText } from 'lucide-react';
import { PetAvatar } from '@/components/PetAvatar';
import { TappableCard } from '@/utils/animations';
import { DoodleStar, DoodleAtom, DoodleArrow } from '@/components/Doodles';
import DreamSection from '@/components/DreamSection';
import ErrorBoundary from '@/components/ErrorBoundary';
import { EVOLUTION_STAGES, PET_MOODS, getPetMessage, starsToNextStage, getStageFromStars, computePetMood } from '@/constants/petConstants';

const LANGUAGES = [
  { code: 'te',    label: 'Telugu',      native: 'తెలుగు' },
  { code: 'hi',    label: 'Hindi',       native: 'हिन्दी' },
  { code: 'en-IN', label: 'Indian English', native: 'English' },
  { code: 'ta',    label: 'Tamil',       native: 'தமிழ்' },
  { code: 'kn',    label: 'Kannada',     native: 'ಕನ್ನಡ' },
  { code: 'ml',    label: 'Malayalam',   native: 'മലയാളം' },
  { code: 'mr',    label: 'Marathi',     native: 'मराठी' },
  { code: 'gu',    label: 'Gujarati',    native: 'ગુજરાતી' },
  { code: 'or',    label: 'Odia',        native: 'ଓଡ଼ିଆ' },
  { code: 'as',    label: 'Assamese',    native: 'অসমীয়া' },
  { code: 'bn',    label: 'Bengali',     native: 'বাংলা' },
  { code: 'mni',   label: 'Manipuri',    native: 'মণিপুরী' },
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MYSTERIES = [
  "Why does the moon always show the same face to Earth? 🌕",
  "How do trees know when to drop their leaves? 🍂",
  "Why does ice float on water instead of sinking? 🧊",
  "How do birds know where to fly during migration? 🐦",
  "Why do we dream while sleeping? 💭",
];

const GoalRow = ({ done, label }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
    <div style={{
      width: 16, height: 16, borderRadius: 8, flexShrink: 0,
      background: done ? '#66BB6A' : 'transparent',
      border: done ? 'none' : '1.5px dashed #AAAAAA',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      {done && <span style={{ fontSize: 14, color: '#fff', fontWeight: 600 }}>✓</span>}
    </div>
    <span style={{ fontSize: 15, color: done ? '#1A1A2E' : '#4A4A5A' }}>
      {label}
    </span>
  </div>
);

// Maps old API mood values → new mood keys
const MOOD_TO_STATE = {
  thriving:    'celebrating',
  happy:       'happy',
  missing:     'lonely',
  sleepy:      'sleeping',
  excited:     'excited',
  curious:     'curious',
  lonely:      'lonely',
  sleeping:    'sleeping',
  celebrating: 'celebrating',
};

// Progress ring around pet
function PetProgressRing({ petStars, starsNeeded, size = 180 }) {
  const r = (size / 2) - 8;
  const circ = 2 * Math.PI * r;
  const pct = starsNeeded > 0 ? Math.min(petStars / starsNeeded, 1) : 1;
  const offset = circ * (1 - pct);
  return (
    <svg
      width={size + 16}
      height={size + 16}
      viewBox={`0 0 ${size + 16} ${size + 16}`}
      style={{ position: 'absolute', top: -8, left: -8, pointerEvents: 'none' }}
    >
      <circle
        cx={(size + 16) / 2} cy={(size + 16) / 2} r={r}
        fill="none" stroke="rgba(0,0,0,0.06)" strokeWidth={6}
      />
      <circle
        cx={(size + 16) / 2} cy={(size + 16) / 2} r={r}
        fill="none" stroke="#448AFF" strokeWidth={4}
        strokeDasharray={circ}
        strokeDashoffset={offset}
        strokeLinecap="round"
        style={{
          transform: 'rotate(-90deg)',
          transformOrigin: 'center',
          transition: 'stroke-dashoffset 1s ease',
        }}
      />
    </svg>
  );
}

// ─── Language Bottom Sheet ──────────────────────────────────────
function LanguageSheet({ open, onClose, selectedLang, onSelect }) {
  if (!open) return null;
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 999,
        background: 'rgba(0,0,0,0.4)',
        transition: 'opacity 0.2s',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          background: '#FFFFFF',
          borderRadius: '20px 20px 0 0',
          padding: '20px 16px',
          paddingBottom: 'calc(56px + env(safe-area-inset-bottom, 20px))',
          maxHeight: '70vh', overflowY: 'auto',
          transform: open ? 'translateY(0)' : 'translateY(100%)',
          transition: 'transform 0.3s ease',
        }}
      >
        {/* Drag handle */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
          <div style={{ width: 40, height: 4, borderRadius: 2, background: '#D1CEC6' }} />
        </div>
        <p style={{ fontSize: 16, fontWeight: 600, color: '#2C2C2A', margin: '0 0 4px', fontFamily: "'Inter', sans-serif" }}>
          Story language
        </p>
        <p style={{ fontSize: 13, color: '#888780', margin: '0 0 16px', fontFamily: "'Inter', sans-serif" }}>
          Priya & Bunty will tell your story in this language
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {LANGUAGES.map(lang => {
            const active = selectedLang === lang.code;
            return (
              <button
                key={lang.code}
                onClick={() => { onSelect(lang.code); onClose(); }}
                style={{
                  padding: '12px 16px', borderRadius: 12, textAlign: 'left',
                  border: active ? '2px solid #448AFF' : '2px dashed #E8E4DA',
                  background: active ? '#E6F1FB' : '#FEFCF7',
                  cursor: 'pointer', position: 'relative',
                }}
              >
                {active && (
                  <span style={{ position: 'absolute', top: 8, right: 10, fontSize: 14, color: '#448AFF' }}>✓</span>
                )}
                <div style={{ fontSize: 16, fontWeight: 500, color: '#2C2C2A', fontFamily: "'Inter', sans-serif" }}>
                  {lang.native}
                </div>
                <div style={{ fontSize: 12, color: '#888780', marginTop: 2, fontFamily: "'Inter', sans-serif" }}>
                  {lang.label}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function HomeScreen({ user, onGenerateStory, masteryTopics, onSnapClick, dailyGoals, petMood }) {
  const petType     = localStorage.getItem('you_pet_type') || 'elephant';
  const petName     = localStorage.getItem('you_pet_name') || 'your pet';
  const petStars    = parseInt(localStorage.getItem('you_pet_stars') || '0');
  const petStage    = parseInt(localStorage.getItem('you_pet_stage') || '1');
  const tapCount    = useRef(0);
  const [tapMood, setTapMood]     = useState(null);

  // Derive mood: prefer prop from backend, else compute from localStorage
  const lastSession = localStorage.getItem('you_last_session_at');
  const derivedMood = MOOD_TO_STATE[petMood] || computePetMood(lastSession);
  const petMoodKey  = tapMood || derivedMood || 'happy';

  // Stars progress
  const nextStage = EVOLUTION_STAGES.find(s => s.totalStars > petStars);
  const starsNeeded = nextStage ? nextStage.totalStars : EVOLUTION_STAGES[EVOLUTION_STAGES.length - 1].totalStars;
  const starsLeft   = starsToNextStage(petStars);

  const proximityMsg = (() => {
    if (starsLeft === 0) return `${petName} has reached Legendary! 🌟`;
    if (starsLeft === 1) return `ONE MORE STAR! ${petName} is shaking with excitement!`;
    if (starsLeft <= 4)  return `Almost there! Just ${starsLeft} more ⭐!`;
    if (starsLeft <= 14) return `Almost there! Just ${starsLeft} more ⭐!`;
    return `${starsLeft} more ⭐ to evolve!`;
  })();

  // Tap micro-interaction cycles through moods
  const handlePetTap = () => {
    const cycle = ['excited', 'celebrating', 'happy'];
    tapCount.current = (tapCount.current + 1) % cycle.length;
    setTapMood(cycle[tapCount.current]);
    setTimeout(() => setTapMood(null), 1500);
  };
  const [boardRaw, setBoardRaw] = useState(() => localStorage.getItem('you_board') || 'cbse');
  const [classRaw, setClassRaw] = useState(() => localStorage.getItem('you_class') || 'class_5_6');
  const classNum    = localStorage.getItem('you_class_number')
    || { class_1_2:'1', class_3_4:'3', class_5_6:'5', class_7_8:'7' }[classRaw]
    || '6';

  // Re-sync board/class from localStorage when user changes them in Profile
  const syncBoardClass = useCallback(() => {
    const b = localStorage.getItem('you_board') || 'cbse';
    const c = localStorage.getItem('you_class') || 'class_5_6';
    if (b !== boardRaw) setBoardRaw(b);
    if (c !== classRaw) setClassRaw(c);
  }, [boardRaw, classRaw]);

  useEffect(() => {
    window.addEventListener('storage', syncBoardClass);
    window.addEventListener('focus', syncBoardClass);
    return () => { window.removeEventListener('storage', syncBoardClass); window.removeEventListener('focus', syncBoardClass); };
  }, [syncBoardClass]);
  const petMsg      = getPetMessage(classNum, petName, { starsToNext: starsLeft });

  const [customTopic, setCustomTopic] = useState('');
  const [mystery] = useState(() => MYSTERIES[Math.floor(Math.random() * MYSTERIES.length)]);
  const contentFileRef = useRef(null);
  const [contentUploadState, setContentUploadState] = useState('idle'); // idle | extracting | success | error
  const [contentUploadMsg, setContentUploadMsg] = useState('');
  const [contentFileName, setContentFileName] = useState('');

  // Client-side image resize before upload (saves bandwidth on 2G/3G)
  const resizeImageIfNeeded = useCallback(async (file) => {
    if (file.type === 'application/pdf') return file;
    if (file.size <= 1 * 1024 * 1024) return file; // under 1MB, skip resize
    return new Promise((resolve) => {
      const img = new window.Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(url);
        const canvas = document.createElement('canvas');
        const MAX = 1600;
        let w = img.width, h = img.height;
        if (w > MAX || h > MAX) {
          const scale = MAX / Math.max(w, h);
          w = Math.round(w * scale);
          h = Math.round(h * scale);
        }
        canvas.width = w;
        canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        canvas.toBlob((blob) => {
          resolve(new File([blob], file.name, { type: 'image/jpeg' }));
        }, 'image/jpeg', 0.85);
      };
      img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
      img.src = url;
    });
  }, []);

  const handleContentUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    if (!allowed.includes(file.type)) {
      setContentUploadState('error');
      setContentUploadMsg('Only JPG, PNG, WebP, or PDF files work');
      setTimeout(() => { setContentUploadState('idle'); setContentUploadMsg(''); }, 3000);
      return;
    }

    const isPdf = file.type === 'application/pdf';
    const maxSize = isPdf ? 10 * 1024 * 1024 : 5 * 1024 * 1024;
    if (file.size > maxSize) {
      setContentUploadState('error');
      setContentUploadMsg(isPdf ? 'PDF must be under 10MB' : 'Image must be under 5MB');
      setTimeout(() => { setContentUploadState('idle'); setContentUploadMsg(''); }, 3000);
      return;
    }

    const truncName = file.name.length > 20 ? file.name.slice(0, 17) + '...' : file.name;
    setContentFileName(truncName);
    setContentUploadState('extracting');
    setContentUploadMsg(isPdf ? 'Extracting text from PDF...' : 'Reading your textbook page...');

    try {
      const resized = await resizeImageIfNeeded(file);
      const formData = new FormData();
      formData.append('file', resized);

      const token = Object.keys(localStorage).find(k => k.startsWith('sb-') && k.includes('auth-token'));
      const session = token ? JSON.parse(localStorage.getItem(token)) : null;
      const headers = {};
      if (session?.access_token) headers['Authorization'] = `Bearer ${session.access_token}`;

      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 30000);

      const res = await fetch(`${API}/upload-content`, {
        method: 'POST',
        headers,
        body: formData,
        signal: controller.signal,
      });
      clearTimeout(timeout);

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Could not read the file');
      }

      const data = await res.json();
      const extractedText = data.extracted_text.slice(0, 500);
      setCustomTopic(extractedText);
      setContentUploadState('success');
      setContentUploadMsg('Generating story...');
      setTimeout(() => { setContentUploadState('idle'); setContentUploadMsg(''); setContentFileName(''); }, 2000);
      // Auto-generate story from extracted text
      onGenerateStory(extractedText);
    } catch (err) {
      setContentUploadState('error');
      setContentUploadMsg(
        err.name === 'AbortError'
          ? 'Taking too long. Check your internet and try again.'
          : (err.message || 'Something went wrong. Try again.')
      );
      setTimeout(() => { setContentUploadState('idle'); setContentUploadMsg(''); setContentFileName(''); }, 3000);
    }

    if (contentFileRef.current) contentFileRef.current.value = '';
  }, [resizeImageIfNeeded, onGenerateStory]);
  const [showLangSheet, setShowLangSheet] = useState(false);
  const [selectedLang, setSelectedLang] = useState(
    () => localStorage.getItem('you_language') || 'te'
  );

  const handleLangChange = (code) => {
    setSelectedLang(code);
    localStorage.setItem('you_language', code);
  };

  const currentLang = LANGUAGES.find(l => l.code === selectedLang);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && customTopic.trim()) {
      onGenerateStory(customTopic.trim());
      setCustomTopic('');
    }
  };

  return (
    <div style={{ padding: '12px 16px 140px', maxWidth: 720, margin: '0 auto', width: '100%' }}>

      {/* ── Section 2: Pet Hero ──────────────────────────────────── */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 16, position: 'relative' }}>
        {/* Doodle decorations */}
        <div style={{ position: 'absolute', top: 8, right: 8, pointerEvents: 'none' }}>
          <DoodleStar color="#448AFF" opacity={0.2} size={14} />
        </div>
        <div style={{ position: 'absolute', bottom: 24, left: 8, pointerEvents: 'none' }}>
          <DoodleAtom color="#66BB6A" opacity={0.15} size={12} />
        </div>
        {/* Pet + progress ring */}
        <div style={{ position: 'relative', width: 180, height: 180 }}>
          <PetProgressRing petStars={petStars} starsNeeded={starsNeeded} size={180}/>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
            <PetAvatar
              petType={petType}
              stage={petStage}
              mood={petMoodKey}
              size="xl"
              animated={true}
              onTap={handlePetTap}
            />
          </div>
        </div>

        {/* Mood message */}
        <p style={{ fontSize: 20, fontFamily: "'Patrick Hand', cursive", color: '#448AFF', textAlign: 'center', marginTop: 8, marginBottom: 6 }}>
          {PET_MOODS[petMoodKey]?.message(petName) || `${petName} is happy to see you!`}
        </p>

        {/* Star count */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 4 }}>
          <span style={{ fontSize: 20, fontFamily: "'Patrick Hand', cursive", color: '#E6A800', fontWeight: 400 }}>
            {petStars} / {starsNeeded} ⭐
          </span>
          <span style={{ fontSize: 17, fontFamily: "'Patrick Hand', cursive", color: '#1A1A2E', fontWeight: 400 }}>
            {EVOLUTION_STAGES[petStage - 1]?.name || 'Baby'}
          </span>
        </div>

        {/* Proximity message */}
        <div style={{
          fontSize: 18, fontFamily: "'Patrick Hand', cursive", color: starsLeft <= 4 ? '#448AFF' : '#1A1A2E',
          fontWeight: 400,
          textAlign: 'center',
        }}>
          {proximityMsg}
        </div>
      </div>

      {/* ── Section 3: Hero Card ──────────────────────────────── */}
      <div style={{
        background: '#FFFFFF',
        borderRadius: 4, padding: '20px 16px',
        border: '1.5px dashed #CCCCCC',
        borderLeft: '3px solid #448AFF',
        boxShadow: 'none',
        marginBottom: 16,
        position: 'relative',
      }}>
        {/* Doodle near headline */}
        <div style={{ position: 'absolute', top: 12, right: 12, pointerEvents: 'none' }}>
          <DoodleArrow color="#FF6B6B" opacity={0.2} size={12} />
        </div>
        {/* Headline */}
        <p style={{ fontSize: 22, fontFamily: "'Patrick Hand', cursive", color: '#1A1A2E', textAlign: 'center', margin: '0 0 4px' }}>
          What do you want to learn today?
        </p>
        <p style={{ fontSize: 14, color: '#555555', textAlign: 'center', margin: '0 0 4px' }}>
          Get ready for Fun Learning from Priya &amp; Bunty
        </p>
        <p style={{ fontSize: 12, color: '#999999', textAlign: 'center', margin: '0 0 10px',
          fontFamily: "'Inter', sans-serif" }}>
          Type any school topic and listen!
        </p>

        {/* Language inline sentence */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
          <span style={{ fontSize: 15, color: '#444441', fontWeight: 400, fontFamily: "'Inter', sans-serif" }}>Learn in</span>
          <button
            data-testid="language-chip"
            onClick={() => setShowLangSheet(true)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: '#FFF3E0', border: '1.5px solid #F5A623',
              borderRadius: 20, padding: '6px 14px 6px 10px',
              fontFamily: "'Patrick Hand', cursive", fontSize: 15, fontWeight: 500,
              color: '#BA7517', cursor: 'pointer',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#BA7517" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10A15.3 15.3 0 0 1 12 2z"/>
            </svg>
            {currentLang?.label?.includes(currentLang?.native) || currentLang?.native?.includes(currentLang?.label)
              ? (currentLang?.label || 'Indian English')
              : `${currentLang?.native || 'తెలుగు'} ${currentLang?.label || 'Telugu'}`}
            <svg width="10" height="10" viewBox="0 0 10 10" fill="#BA7517"><path d="M2 3.5L5 7L8 3.5" stroke="#BA7517" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>
        </div>
        <LanguageSheet open={showLangSheet} onClose={() => setShowLangSheet(false)} selectedLang={selectedLang} onSelect={handleLangChange} />

        {/* Input + camera/PDF upload row */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
          <input
            type="file"
            ref={contentFileRef}
            onChange={handleContentUpload}
            accept="image/jpeg,image/png,image/webp,application/pdf"
            style={{ display: 'none' }}
            aria-label="Upload textbook photo or PDF"
          />
          {contentUploadState === 'extracting' ? (
            /* Extraction progress state */
            <div
              style={{
                flex: 1, padding: '10px 12px', borderRadius: 8,
                background: 'rgba(68,138,255,0.06)',
                border: '1.5px dashed #448AFF',
                display: 'flex', alignItems: 'center', gap: 8,
              }}
              aria-live="polite"
            >
              {contentFileName.toLowerCase().includes('.pdf') ? (
                <FileText size={16} color="#448AFF" />
              ) : (
                <Camera size={16} color="#448AFF" />
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, color: '#448AFF', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {contentFileName}
                </div>
                <div style={{ fontSize: 11, color: '#666666', marginTop: 2 }}>
                  {contentUploadMsg}
                </div>
              </div>
              {/* Pulsing dot */}
              <div style={{
                width: 8, height: 8, borderRadius: '50%', background: '#448AFF', flexShrink: 0,
                animation: 'pulse 1.5s infinite',
              }} />
              <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }`}</style>
            </div>
          ) : (
            /* Normal input or success/error state */
            <input
              type="text"
              placeholder="Type a topic like 'Water Cycle'..."
              value={customTopic}
              onChange={e => setCustomTopic(e.target.value)}
              onKeyDown={handleKeyDown}
              className="doodle-input"
              style={{
                flex: 1,
                borderColor: contentUploadState === 'success' ? '#66BB6A'
                           : contentUploadState === 'error' ? '#FF6B6B'
                           : undefined,
                transition: 'border-color 0.3s',
              }}
            />
          )}
          {/* Upload button — camera + PDF badge */}
          <div
            onClick={() => contentFileRef.current?.click()}
            style={{
              width: 46, height: 46, borderRadius: 12,
              background: contentUploadState === 'extracting'
                ? 'rgba(68,138,255,0.15)'
                : 'rgba(68,138,255,0.08)',
              border: '1.5px dashed #448AFF',
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center',
              cursor: contentUploadState === 'extracting' ? 'default' : 'pointer',
              flexShrink: 0, position: 'relative',
              opacity: contentUploadState === 'extracting' ? 0.5 : 1,
            }}
            role="button"
            aria-label="Snap your textbook or upload PDF"
          >
            <Camera size={18} color="#448AFF" strokeWidth={2} />
            <span style={{ fontSize: 7, color: '#448AFF', fontWeight: 600, marginTop: 1 }}>Snap</span>
            <span style={{ fontSize: 7, color: '#448AFF', fontWeight: 600, marginTop: -1 }}>PDF</span>
          </div>
        </div>
        {/* Upload status message below input */}
        {contentUploadMsg && contentUploadState !== 'extracting' && (
          <div
            aria-live="polite"
            style={{
              fontSize: 11, marginTop: -8, marginBottom: 8, paddingLeft: 4,
              color: contentUploadState === 'success' ? '#2E7D32' : '#D32F2F',
              fontWeight: 500,
            }}
          >
            {contentUploadState === 'success' ? '✓ ' : ''}{contentUploadMsg}
          </div>
        )}

        {/* Generate button — full width, primary CTA */}
        <button
          onClick={() => customTopic.trim() && onGenerateStory(customTopic.trim())}
          className="doodle-btn-primary"
          style={{
            width: '100%', padding: 15,
            opacity: 1,
            cursor: customTopic.trim() ? 'pointer' : 'default',
            transition: 'opacity 0.2s ease',
          }}
        >
          Generate Audio Story
        </button>
      </div>

      {/* ── Section 4: iki's Dreams ───────────────────────────── */}
      <ErrorBoundary>
        <DreamSection user={user} onGenerateStory={onGenerateStory} />
      </ErrorBoundary>

      {/* ── Section 5: Continue Learning ─────────────────────── */}
      {masteryTopics && masteryTopics.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <p style={{ fontSize: 14, color: '#666666', marginBottom: 8, marginTop: 0 }}>
            Continue learning:
          </p>
          {masteryTopics.slice(0, 3).map((t, i) => (
            <TappableCard
              key={i}
              onClick={() => onGenerateStory(t.content_preview)}
              style={{
                background: '#FFFFFF', borderRadius: 4, padding: '10px 12px',
                marginBottom: 6, border: '1.5px dashed #CCCCCC',
                borderLeft: '3px solid #66BB6A',
                boxShadow: 'none',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                cursor: 'pointer',
              }}
            >
              <span style={{ fontSize: 14, color: '#555555', flex: 1 }}>
                {(t.content_preview || 'Topic').slice(0, 35)}
                {(t.content_preview?.length || 0) > 35 ? '...' : ''}
              </span>
              <span style={{ fontSize: 14, fontFamily: "'Patrick Hand', cursive", color: '#448AFF', marginLeft: 8, flexShrink: 0 }}>
                {t.mastery_pct}%
              </span>
            </TappableCard>
          ))}
        </div>
      )}

      {/* ── Section 6: Daily Goals ────────────────────────────── */}
      <div style={{ background: '#FFFFFF', borderRadius: 4, padding: 12, marginBottom: 14, boxShadow: 'none', border: '1.5px dashed #CCCCCC', borderLeft: '3px solid #FFD740' }}>
        <p style={{ fontSize: 14, fontFamily: "'Patrick Hand', cursive", color: '#1A1A2E', marginBottom: 10, marginTop: 0 }}>
          Today's Goals
        </p>
        <GoalRow done={dailyGoals?.listened} label="Listen to 1 story" />
        <GoalRow done={dailyGoals?.quizzed} label="Score 2+ on quiz" />
        <div style={{ marginBottom: 0 }}>
          <GoalRow done={dailyGoals?.asked} label="Ask Priya 1 question" />
        </div>
      </div>

      {/* ── Section 7: Mystery (compact row) ─────────────────── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        background: '#FFFFFF', borderRadius: 4, padding: '10px 12px',
        border: '1.5px dashed #CCCCCC',
        borderLeft: '3px solid #AB47BC',
        boxShadow: 'none',
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontSize: 14, fontFamily: "'Patrick Hand', cursive", color: '#AB47BC', letterSpacing: 1, margin: 0 }}>
            MYSTERY
          </p>
          <p style={{ fontSize: 14, color: '#555555', marginTop: 2, marginBottom: 0, lineHeight: 1.4 }}>
            {mystery}
          </p>
        </div>
        <button
          onClick={() => onGenerateStory(mystery)}
          style={{
            width: 44, height: 44, flexShrink: 0,
            background: 'transparent',
            border: '1.5px dashed #448AFF',
            borderRadius: 8, color: '#448AFF', fontSize: 14, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          ▶
        </button>
      </div>

    </div>
  );
}
